import { createContext, ReactNode, useEffect, useState } from "react";

import { parseCookies, setCookie } from "nookies";

import EnUSTranslation from "@/constants/Translations/en-US";
import EsTranslation from "@/constants/Translations/es";
import PtBRTranslation from "@/constants/Translations/pt-BR";
import ITranslation from "@/interfaces/ITranslation";
import { FlushCookies } from "@/utils/ReactNativeUtils";

const translations = {
  "pt-BR": PtBRTranslation,
  "en-US": EnUSTranslation,
  es: EsTranslation,
};

interface LanguageContextData {
  translation: ITranslation;
  language: string;
  setLanguage: (language: string) => void;
}

export const DEFAULT_LANGUAGE = "pt-BR";

export const LanguageContext = createContext({} as LanguageContextData);

interface LanguageProviderProps {
  language?: string;
  defaultLanguage?: string;
  children: ReactNode;
}

export function LanguageProvider({
  language: initialLanguage,
  defaultLanguage = DEFAULT_LANGUAGE,
  children,
}: LanguageProviderProps) {
  const [language, setLanguage] = useState(() => {
    if (initialLanguage) return initialLanguage;
    const cookies = parseCookies();
    if (cookies.language) return cookies.language;
    if (typeof window !== "undefined" && navigator?.language) return navigator.language;
    return defaultLanguage;
  });

  const [translation, setTranslation] = useState(
    translations[language] || translations[DEFAULT_LANGUAGE]
  );

  useEffect(() => {
    import(`../constants/Translations/${language}.tsx`)
      .then((module) => {
        setTranslation(module.default);
      })
      .catch(() => {
        setLanguage(defaultLanguage);
      });

    setCookie(null, "language", language, {
      sameSite: "Strict",
      maxAge: 365 * 24 * 60 * 60,
      path: "/",
    });
    FlushCookies();

    document.documentElement.setAttribute("lang", language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ translation, language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
